import React, { useContext, useEffect, useState, useMemo } from "react";
import { Link } from "wouter";
import Swal from "sweetalert2";
import { EnvelopeIcon, LinkIcon, MagnifyingGlassIcon, XCircleIcon, XMarkIcon, InboxIcon } from "@heroicons/react/24/outline";

import { AuthContext } from "../contexts/auth";
import PopoverComponentCustom from "../components/popover_component";
import Footer from "../components/footer";
import Fetching from "../components/fetching";
import Empty from "../components/empty";
import { getNfcTagList, patchNfcTagWorkingArea, requestNfcTags } from "../services/api_nfc_tags";
import { getAvailableWorkingAreaList } from "../services/api_working_area";
import { estraiMessaggiErrore } from "../services/utils";
import ComboboxAutocomplete from './combobox_autocomplete';
import TapAndPlayIcon from '@mui/icons-material/TapAndPlay';
import PrettyPagination from "../components/pretty_pagination";
import V2ListBoxPageSize from "../components/v2_listbox_page_size";
import LoadingState from "./fetchingV2";


const EMPTY_REQUEST_FORM = {
  quantity: 1,
  shipping_address: "",
};


export default function WorkingAreaNfc() {
  const { profile, token } = useContext(AuthContext);
  const isProfileComplete = profile?.has_complete_profile;
  const companyEmail = profile?.company?.email || "";
  const [tags, setTags] = useState([]);
  const [workingAreas, setWorkingAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingAreas, setLoadingAreas] = useState(false);
  const [search, setSearch] = useState("");
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestForm, setRequestForm] = useState(EMPTY_REQUEST_FORM);
  const [requestErrors, setRequestErrors] = useState({});
  const [requesting, setRequesting] = useState(false);

  const [showAreaModal, setShowAreaModal] = useState(false);
  const [selectedTag, setSelectedTag] = useState(null);
  const [selectedWorkingArea, setSelectedWorkingArea] = useState(null);
  const [areaError, setAreaError] = useState("");
  const [savingArea, setSavingArea] = useState(false);
  const [removingTagId, setRemovingTagId] = useState(null);

  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalCount, setTotalCount] = useState(0);

  const totalPages = Math.ceil(totalCount / pageSize);
  
  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearch(search.trim());
      setPage(1);
    }, 400);

    return () => clearTimeout(timeout);
  }, [search]);

  const fetchTags = async () => {
    if (!token) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await getNfcTagList(token, {
        search: debouncedSearch || undefined,
        page,
        page_size: pageSize,
      });

      if (Array.isArray(response)) {
        setTags(response);
        setTotalCount(response.length);
      } else {
        setTags(response?.results || []);
        setTotalCount(response?.count || 0);
      }
    } catch (error) {
      console.error("Errore caricamento NFC", error);

      setTags([]);
      setTotalCount(0);

      Swal.fire({
        icon: "error",
        title: "Errore",
        text: estraiMessaggiErrore(error),
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, [token, page, pageSize, debouncedSearch]);


  const fetchWorkingAreas = async (nfcTagId) => {
    if (!token || !nfcTagId) { return [];}

    setLoadingAreas(true);

    try {
      const response = await getAvailableWorkingAreaList(token, nfcTagId, 'shifts');

      const areas = Array.isArray(response) ? response : response?.results || [];

      setWorkingAreas(areas);

      return areas;
    } catch (error) {
      console.error("Errore caricamento aree di lavoro", error);

      Swal.fire({
        icon: "error",
        title: "Errore",
        text: estraiMessaggiErrore(error),
      });

      return [];
    } finally {
      setLoadingAreas(false);
    }
  };

  const openRequestModal = () => {
    setRequestForm(EMPTY_REQUEST_FORM);
    setRequestErrors({});
    setShowRequestModal(true);
  };


  const closeRequestModal = () => {
    if (requesting) {
      return;
    }

    setRequestForm(EMPTY_REQUEST_FORM);
    setRequestErrors({});
    setShowRequestModal(false);
  };


  const clearRequestError = (field) => {
    setRequestErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }

      const next = { ...prev };
      delete next[field];

      return next;
    });
  };


  const validateRequestForm = () => {
    const nextErrors = {};

    const quantity = Number(requestForm.quantity);
    const shippingAddress = requestForm.shipping_address.trim();

    if (!Number.isInteger(quantity) || quantity < 1) {
      nextErrors.quantity = "Inserisci una quantità valida";
    }

    if (!shippingAddress) {
      nextErrors.shipping_address = "L'indirizzo di spedizione è obbligatorio";
    }

    setRequestErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  };


  const handleRequestNfc = async () => {
    if (!validateRequestForm()) {
      return;
    }

    setRequesting(true);
    setRequestErrors({});

    const payload = {
      quantity: Number(requestForm.quantity),
      shipping_address: requestForm.shipping_address.trim(),
    };

    try {
      await requestNfcTags(token, payload);

      setShowRequestModal(false);
      setRequestForm(EMPTY_REQUEST_FORM);
      setRequestErrors({});

      await Swal.fire({
        icon: "success",
        title: "Richiesta inviata",
        text: companyEmail
          ? `La richiesta è stata inviata. Una copia è stata spedita anche a ${companyEmail}`
          : "La richiesta è stata inviata correttamente.",
        confirmButtonText: "Chiudi",
      });
    } catch (error) {
      console.error("Errore richiesta NFC", error);

      const responseErrors = error?.response?.data;

      if (responseErrors && typeof responseErrors === "object" && !Array.isArray(responseErrors)
      ) {
        setRequestErrors(responseErrors);
      }

      Swal.fire({
        icon: "error",
        title: "Richiesta non inviata",
        text: estraiMessaggiErrore(error),
      });
    } finally {
      setRequesting(false);
    }
  };

  const openAreaModal = async (nfcTag) => {
    setSelectedTag(nfcTag);
    setSelectedWorkingArea(null);
    setAreaError("");
    setShowAreaModal(true);

    const areas = await fetchWorkingAreas(nfcTag.id);

    const currentArea = areas.find((area) => Number(area.id) === Number(nfcTag.working_area));

    setSelectedWorkingArea(currentArea || null);
  };

  const closeAreaModal = () => {
    if (
      savingArea ||
      removingTagId === selectedTag?.id
    ) {
      return;
    }

    setShowAreaModal(false);
    setSelectedTag(null);
    setSelectedWorkingArea(null);
    setWorkingAreas([]);
    setAreaError("");
  };


  const handleSaveWorkingArea = async () => {
    if (!selectedTag) {return;}

    const workingAreaId = selectedWorkingArea?.id ?? null;

    setSavingArea(true);
    setAreaError("");

    try {
      const updatedTag = await patchNfcTagWorkingArea(token, selectedTag.id, workingAreaId);
      await fetchTags();

      setShowAreaModal(false);
      setSelectedTag(null);
      setSelectedWorkingArea(null);
      setWorkingAreas([]);
      setAreaError("");

      Swal.fire({
        icon: "success",
        title: workingAreaId ? "Area associata" : "Area scollegata",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Errore associazione area NFC", error);

      const responseErrors = error?.response?.data;

      if (responseErrors?.working_area) {
        setAreaError(
          Array.isArray(responseErrors.working_area)
            ? responseErrors.working_area[0]
            : responseErrors.working_area
        );
      } else {
        setAreaError(estraiMessaggiErrore(error));
      }
    } finally {
      setSavingArea(false);
    }
  };

  const handleRemoveWorkingArea = async (nfcTag, { closeModal = false } = {}) => {
    if (!nfcTag?.working_area || removingTagId !== null) {
      return;
    }

    const confirmation = await Swal.fire({
      icon: "warning",
      title: "Rimuovere l'associazione?",
      text: `Il tag NFC "${nfcTag.name || nfcTag.token}" non sarà più collegato all'area "${nfcTag.working_area_name || ""}".`,
      showCancelButton: true,
      confirmButtonText: "Rimuovi",
      cancelButtonText: "Annulla",
      confirmButtonColor: "#dc2626",
      reverseButtons: true,
    });

    if (!confirmation.isConfirmed) {
      return;
    }

    setRemovingTagId(nfcTag.id);
    setAreaError("");

    try {
      const updatedTag = await patchNfcTagWorkingArea(token, nfcTag.id, null);

      await fetchTags();

      if (closeModal) {
        setShowAreaModal(false);
        setSelectedTag(null);
        setSelectedWorkingArea(null);
        setWorkingAreas([]);
        setAreaError("");
      }

      await Swal.fire({
        icon: "success",
        title: "Associazione rimossa",
        timer: 1200,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Errore rimozione associazione NFC", error);

      const responseErrors = error?.response?.data;

      const message = responseErrors?.working_area
        ? Array.isArray(responseErrors.working_area)
          ? responseErrors.working_area[0]
          : responseErrors.working_area
        : estraiMessaggiErrore(error);

      if (closeModal) {
        setAreaError(message);
      } else {
        Swal.fire({
          icon: "error",
          title: "Associazione non rimossa",
          text: message,
        });
      }
    } finally {
      setRemovingTagId(null);
    }
  };

  return (
    <div className="flex min-h-screen flex-col">
      <PopoverComponentCustom title="Gestione NFC" />

      {profile?.customer_type === "company" && !isProfileComplete && profile?.company?.email === profile?.the_real_user_email && (
        <div className="w-full">
          <div className="mb-6 bg-orange-100 p-4 text-orange-600">
            <p className="text-center text-sm font-semibold">
              Il profilo non è completo.<br />Compila i campi mancanti{" "}
              <Link to="/profilo/fatturazione" className="text-indigo-600 underline hover:text-indigo-800">cliccando qui</Link>
            </p>
          </div>
        </div>
      )}

      <main className="flex-grow py-8">
        <div className="mx-auto w-full max-w-[1600px] px-4 md:px-6">
          <div className="mb-5">
            <Link to="/working_area/manage" className="text-sm font-semibold text-indigo-600 hover:underline">← Torna alle Aree di Lavoro</Link>
          </div>

          <div className="rounded-xl border border-indigo-100 bg-white shadow-sm ring-1 ring-black/5">
            <div className="flex flex-col gap-4 px-5 py-4 md:flex-row md:items-center md:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600">
                  <TapAndPlayIcon className="!h-[22px] !w-[22px]" />
                </div>

                <div>
                  <h1 className="text-sm font-semibold uppercase text-gray-800">Tag NFC</h1>
                  <p className="mt-0.5 text-xs text-gray-500">Visualizza i tag NFC acquistati e collegali alle aree di lavoro</p>
                </div>
              </div>

              <button
                type="button"
                onClick={openRequestModal}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500"
              >
                <EnvelopeIcon className="h-4 w-4" />Richiedi NFC
              </button>
            </div>

            <div className="border-t border-gray-100" />

            {/* Ricerca */}
            <div className="px-5 py-3">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />

                <input
                  type="text"
                  value={search}
                  onChange={(event) =>
                    setSearch(event.target.value)
                  }
                  placeholder="Cerca per nome, identificativo o area..."
                  className="h-9 w-full rounded-lg border border-gray-200 pl-9 pr-10 text-sm text-gray-700 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />

                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label="Cancella ricerca"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            <div className="mt-3 flex items-center justify-between gap-3 bg-gray-50 px-6 py-2 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                <span className="font-semibold text-gray-700">
                  {tags.length}
                </span>{" "}
                di{" "}
                <span className="font-semibold text-gray-700">
                  {totalCount}
                </span>{" "}
                tag NFC
              </p>

              <V2ListBoxPageSize
                value={pageSize}
                onChange={(value) => {
                  setPageSize(Number(value));
                  setPage(1);
                }}
              />
            </div>
          </div>

          {/* Lista NFC */}
          <div className="mt-6 overflow-hidden rounded-xl border border-gray-300 bg-white">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="w-[25%] border-r border-gray-300 px-4 py-2 text-left font-semibold text-gray-600">
                      Nome
                    </th>
                    <th className="w-[30%] border-r border-gray-300 px-4 py-2 text-left font-semibold text-gray-600">
                      Area collegata
                    </th>
                    <th className="w-[15%] px-4 py-2 text-center font-semibold text-gray-600">
                      Azioni
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="3"
                        className="py-6 text-center"
                      >
                        <Fetching />
                      </td>
                    </tr>
                  ) : tags.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-8">
                        <div className="flex flex-col items-center justify-center rounded-xl border-2 border-gray-200 bg-gray-50 px-6 py-10 text-center">
                          <InboxIcon className="mb-4 h-10 w-10 text-gray-400" />

                          <h3 className="text-sm font-semibold text-gray-800">
                            {search
                              ? "Nessun risultato trovato"
                              : "Nessun tag NFC disponibile"}
                          </h3>

                          <p className="mt-2 max-w-md text-sm text-gray-500">
                            {search
                              ? "Prova a modificare i filtri o il testo della ricerca"
                              : "Richiedi nuovi tag NFC per poterli associare alle aree di lavoro"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    tags.map((nfcTag) => (
                      <tr
                        key={nfcTag.id}
                        className="border-t border-gray-300 hover:bg-gray-50"
                      >
                        <td className="whitespace-normal break-words border-r border-gray-300 px-4 py-3 text-gray-800">
                          {nfcTag.name || (
                            <span className="text-gray-400">
                              —
                            </span>
                          )}
                        </td>

                        <td className="whitespace-normal break-words border-r border-gray-300 px-4 py-3 text-gray-800">
                          {nfcTag.working_area_name ? (
                            <span className="inline-flex rounded-full bg-indigo-50 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                              {nfcTag.working_area_name}
                            </span>
                          ) : (
                            <span className="text-gray-400">
                              Non associato
                            </span>
                          )}
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex flex-wrap justify-center gap-x-4 gap-y-2">
                            <button
                              type="button"
                              onClick={() => openAreaModal(nfcTag)}
                              disabled={removingTagId === nfcTag.id}
                              title={
                                nfcTag.working_area
                                  ? "Modifica area"
                                  : "Associa area"
                              }
                              className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                            >
                              <LinkIcon className="h-4 w-4" />

                              {nfcTag.working_area
                                ? "Modifica"
                                : "Associa"}
                            </button>

                            {nfcTag.working_area && (
                              <button
                                type="button"
                                onClick={() => handleRemoveWorkingArea(nfcTag)}
                                disabled={removingTagId === nfcTag.id}
                                title="Rimuovi associazione"
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-red-600 hover:text-red-800 hover:underline disabled:cursor-not-allowed disabled:opacity-50"
                              >
                                <XCircleIcon className="h-4 w-4" />

                                {removingTagId === nfcTag.id
                                  ? "Rimozione..."
                                  : "Rimuovi"}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              {totalPages && totalPages > 1 ? (
                <div className="pb-2 border-t border-gray-200">
                  <PrettyPagination
                    page={page}
                    totalPages={totalPages}
                    totalCount={totalCount}
                    onChange={(newPage) => {
                      setPage(newPage);
                      setShowAreaModal(false);
                      setSelectedTag(null);
                      setSelectedWorkingArea(null);
                      setWorkingAreas([]);
                    }}
                    labelSingolare="tag NFC"
                    labelPlurale="tag NFC"
                    className="mt-6"
                  />
                </div>
              ) : ('')}
            </div>
          </div>
        </div>
      </main>

      {showAreaModal && selectedTag && (
        <div
          className="fixed inset-0 flex items-start justify-center bg-black/50 px-4 py-6 sm:py-10"
          style={{ zIndex: 200 }}
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeAreaModal();
            }
          }}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="associate-area-title"
            className="flex max-h-[calc(100vh-5rem)] w-full max-w-[50%] flex-col overflow-visible rounded-lg bg-white shadow-xl"
          >
            <div className="flex shrink-0 items-start justify-between rounded-t-lg border-b border-gray-200 bg-indigo-50 px-5 py-4">
              <div>
                <h2
                  id="associate-area-title"
                  className="text-sm font-semibold uppercase text-indigo-800"
                >
                  Associa area di lavoro
                </h2>

                <p className="mt-1 text-xs text-gray-500">
                  NFC: {selectedTag.name || selectedTag.token}
                </p>
              </div>

              <button
                type="button"
                onClick={closeAreaModal}
                disabled={savingArea}
                aria-label="Chiudi"
                className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
              <label className="mb-1 block text-sm font-medium text-gray-800">
                Area di lavoro
              </label>

              {loadingAreas ? (
                <LoadingState
                  spinnerSize={30}
                  label="Caricamento..."
                  center={true}
                  fullWidth={true}
                  minHeight="auto"
                  className="inline-block align-middle border border-dashed rounded-md border-gray-300 py-4"
                />
              ) : (
                <ComboboxAutocomplete
                  options={workingAreas}
                  value={selectedWorkingArea}
                  onChange={(area) => {setSelectedWorkingArea(area); setAreaError("");}}
                  placeholder="Cerca un'area di lavoro..."
                  bgActiveColor="bg-indigo-50"
                  textActiveColor="text-indigo-700"
                  borderClass={areaError ? "border-red-400 rounded-md" : "border-gray-200 rounded-md"}
                  positionOptions="relative"
                  openDirection="bottom"
                  zIndex="z-[1000] overflow-visible"
                  displayValue={(area) => area ? area.name : ""}
                  renderOption={(area) => (
                    <div className="flex items-center gap-3">
                      <span className="truncate text-sm text-gray-700">
                        {area.name}
                      </span>
                    </div>
                  )}
                />
              )}

              {areaError && (
                <p className="mt-2 text-xs text-red-600">
                  {areaError}
                </p>
              )}

              {!loadingAreas && workingAreas.length === 0 && (
                <p className="mt-3 text-xs text-gray-500">
                  Non sono presenti aree di lavoro disponibili
                </p>
              )}

              <p className="mt-4 text-xs leading-5 text-gray-500">
                Ogni NFC può essere associato a una sola area di lavoro
              </p>
            </div>

            <div className="flex shrink-0 items-center justify-between gap-3 rounded-b-lg border-t border-gray-200 bg-white px-5 py-4">
              <div>
                {selectedTag.working_area && (
                  <button
                    type="button"
                    onClick={() =>
                      handleRemoveWorkingArea(selectedTag, {
                        closeModal: true,
                      })
                    }
                    disabled={
                      savingArea ||
                      removingTagId === selectedTag.id
                    }
                    className="inline-flex items-center gap-2 rounded-md border border-red-300 bg-white px-4 py-2 text-xs font-semibold text-red-600 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <XCircleIcon className="h-4 w-4" />

                    {removingTagId === selectedTag.id
                      ? "Rimozione..."
                      : "Rimuovi associazione"}
                  </button>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeAreaModal}
                  disabled={
                    savingArea ||
                    removingTagId === selectedTag.id
                  }
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Annulla
                </button>

                <button
                  type="button"
                  onClick={handleSaveWorkingArea}
                  disabled={
                    savingArea ||
                    loadingAreas ||
                    removingTagId === selectedTag.id
                  }
                  className="rounded-md bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {savingArea
                    ? "Salvataggio..."
                    : "Salva associazione"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showRequestModal && (
        <div
          className="fixed inset-0 flex items-start justify-center bg-black/50 px-4 py-6 sm:py-10" style={{ zIndex: 200 }}
          onMouseDown={(event) => {if (event.target === event.currentTarget) {closeRequestModal();}}}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="request-nfc-title"
            className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-xl bg-white shadow-xl"
          >
            <div className="flex items-start justify-between border-b border-gray-200 bg-indigo-50 px-5 py-4">
              <div>
                <h2
                  id="request-nfc-title"
                  className="text-sm font-semibold uppercase text-indigo-800"
                >
                  Richiedi NFC
                </h2>

                <p className="mt-1 text-xs text-gray-500">
                  Invia una richiesta per acquistare nuovi tag NFC
                </p>
              </div>

              <button
                type="button"
                onClick={closeRequestModal}
                disabled={requesting}
                aria-label="Chiudi"
                className="text-gray-400 hover:text-gray-700 disabled:opacity-50"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <div className="overflow-y-auto px-5 py-5">
              <div className="pb-4">
                <p className="text-sm font-medium leading-2 text-gray-700 text-justify">
                  Inserisci la quantità di NFC di cui necessiti e aggiungi l'indirizzo di spedizione. Ti risponderemo comunicandoti il prezzo e il link per effettuare il pagamento.
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-800">Quantità<span className="ml-1 text-red-500">*</span></label>

                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={requestForm.quantity}
                    disabled={requesting}
                    onChange={(event) => {setRequestForm((prev) => ({...prev, quantity: event.target.value,})); clearRequestError("quantity");}}
                    className={`w-full rounded-md border px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 ${
                      requestErrors.quantity ? "border-red-400" : "border-gray-300"}`}
                  />

                  {requestErrors.quantity && (
                    <p className="mt-1 text-xs text-red-600">{Array.isArray(requestErrors.quantity) ? requestErrors.quantity[0] : requestErrors.quantity}</p>
                  )}
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-800">
                    Indirizzo di spedizione<span className="ml-1 text-red-500">*</span>
                  </label>

                  <textarea
                    rows={1}
                    maxLength={500}
                    value={requestForm.shipping_address}
                    disabled={requesting}
                    onChange={(event) => {setRequestForm((prev) => ({...prev, shipping_address: event.target.value,}));
                      clearRequestError("shipping_address");
                    }}
                    placeholder="Es. Via Roma 10, 00100 Roma (RM)"
                    className={`w-full resize-none rounded-md border px-3 py-2 text-sm outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 disabled:bg-gray-100 ${
                      requestErrors.shipping_address ? "border-red-400" : "border-gray-300"}`}
                  />

                  {requestErrors.shipping_address && (
                    <p className="mt-1 text-xs text-red-600">
                      {Array.isArray(requestErrors.shipping_address)
                        ? requestErrors.shipping_address[0]
                        : requestErrors.shipping_address}
                    </p>
                  )}
                </div>
              </div>

              <div className="mt-5 rounded-lg border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs leading-5 text-gray-600">
                  Stai per inviarci una mail con i seguenti dettagli. Una copia della mail verrà inviata anche al tuo indirizzo collegato all'account.
                </p>

                <dl className="mt-3 space-y-2 text-sm">
                  <div className="flex gap-2">
                    <dt className="shrink-0 font-medium text-gray-700">
                      Quantità:
                    </dt>

                    <dd className="text-gray-600">
                      {requestForm.quantity || "—"}
                    </dd>
                  </div>

                  <div className="flex min-w-0 gap-2">
                    <dt className="shrink-0 font-medium text-gray-700">
                      Spedizione:
                    </dt>

                    <dd className="min-w-0 whitespace-pre-wrap break-words text-gray-600">
                      {requestForm.shipping_address.trim() ||
                        "—"}
                    </dd>
                  </div>
                </dl>

                {companyEmail && (
                  <p className="mt-3 break-all text-xs text-gray-500">
                    Copia inviata a:{" "}
                    <span className="font-medium text-gray-700">
                      {companyEmail}
                    </span>
                  </p>
                )}

                {requestErrors.email && (
                  <p className="mt-3 text-xs text-red-600">
                    {Array.isArray(requestErrors.email)
                      ? requestErrors.email[0]
                      : requestErrors.email}
                  </p>
                )}

                {requestErrors.detail && (
                  <p className="mt-3 text-xs text-red-600">
                    {requestErrors.detail}
                  </p>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-2 border-t border-gray-200 bg-white px-5 py-4">
              <button
                type="button"
                onClick={closeRequestModal}
                disabled={requesting}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Annulla
              </button>

              <button
                type="button"
                onClick={handleRequestNfc}
                disabled={requesting}
                className="inline-flex items-center justify-center gap-2 rounded-md bg-indigo-600 px-4 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <EnvelopeIcon className="h-4 w-4" />

                {requesting
                  ? "Invio in corso..."
                  : "Invia richiesta"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}
